import { useLingui } from '@lingui/react'
import { type NowMerchantExtended } from '@redteclab/api/clients/bully'
import { type FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import 'views/components/organisms/NowShippingModal/NowShippingModal.scss'

import {
  CART_GOT_ELIGIBLE_BY_DELETE,
  CART_GOT_ELIGIBLE_BY_QUANTITY_CHANGE,
} from 'shared/experiments/consts/events'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { fetchCartAction } from 'shared/store/ducks/cart'
import {
  type IState,
  type NowDataState,
  type NowDataStateSlot,
} from 'types/redux'
import { validateZipCode } from 'views/components/organisms/NowShippingModal/NowShippingModalContent/index'
import NowZipForm, {
  type INowDeliveryBoxData,
} from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowZipForm'
import { PRODUCTS_NOT_AVAILABLE } from 'views/consts/notEligibleReasons'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

const NowShippingModalCartContent: FC = () => {
  const { i18n } = useLingui()
  const [notEligibleReasonCode, setNotEligibleReasonCode] = useState<number>(0)
  const nowDataState: NowDataState = useSelector(
    (state: IState) => state.nowData
  )
  const [zipCode, setZipCode] = useState(nowDataState?.zipCode ?? '')
  const [errorMessage, setErrorMessage] = useState('')

  const [nowEligible, setNowEligible] = useState(false)
  const [nowDeliveryBoxData, setNowDeliveryBoxData] =
    useState<INowDeliveryBoxData | null>(null)

  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const fetchNowData = (zipCodeToUse: string) => {
    const nowRequestParams = {
      nowZipCode: zipCodeToUse,
      isNowSelected: false,
    }

    dispatch(fetchCartAction(false, false, nowRequestParams, true))
  }

  const handleSubmit = (zip: string) => {
    if (validateZipCode(zipCode, i18n, setErrorMessage, tenant)) {
      if (zipCode) {
        setIsLoading(true)
      }

      try {
        fetchNowData(zip)
      } catch (e) {
        setIsLoading(false)

        setNotEligibleReasonCode(PRODUCTS_NOT_AVAILABLE)
        setNowEligible(false)
      }
    }
  }

  useEffect(() => {
    setIsLoading(false)

    // XXX: is it ever falsy? Empty objects are used as default value everywhere in the Cart Now code
    if (!nowDataState) {
      return
    }

    setNotEligibleReasonCode(nowDataState?.notEligibleReason ?? 0)
    setNowEligible(!!nowDataState.nowEligible)
    setZipCode(nowDataState?.zipCode ?? '')

    if (nowDataState?.nowEligible) {
      switch (nowDataState.eventType) {
        case 'delete':
          trackOptimizelyEvent(CART_GOT_ELIGIBLE_BY_DELETE)
          break
        case 'update':
          trackOptimizelyEvent(CART_GOT_ELIGIBLE_BY_QUANTITY_CHANGE)
          break
      }
    }

    let slots: NowDataStateSlot[] = []
    if (nowDataState?.slots?.length) {
      const slot = nowDataState.slots[0]
      slots = nowDataState.slots.filter(
        item => item.deliveryDay === slot.deliveryDay
      )
    }

    setNowDeliveryBoxData({
      slots: slots as NowDataStateSlot[],
      merchant: { ...nowDataState.merchant } as NowMerchantExtended,
      centsDifferenceMinimumOrderValue:
        nowDataState.centsDifferenceMinimumOrderValue,
    })
  }, [nowDataState])

  return (
    <NowZipForm
      nowEligible={nowEligible}
      zipCode={zipCode}
      setZipCode={setZipCode}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      nowDeliveryBoxData={nowDeliveryBoxData}
      setErrorMessage={setErrorMessage}
      errorMessage={errorMessage}
      notEligibleReasonCode={notEligibleReasonCode}
    />
  )
}

export default withShopAndRuntimeConfig(NowShippingModalCartContent)
